<script setup lang="ts">
import { computed, type PropType, useAttrs } from "vue";
import { type RouteLocationRaw, useLink } from "vue-router";

const props = defineProps({
  to: {
    type: [Object, String] as PropType<RouteLocationRaw>,
    default: null,
  },
  replace: { type: Boolean as PropType<boolean>, default: undefined },
  custom: { type: Boolean as PropType<boolean>, default: undefined },
  activeClass: { type: String as PropType<string>, default: undefined },
  exactActiveClass: { type: String as PropType<string>, default: undefined },
  ariaCurrentValue: {
    type: String as PropType<"page" | "step" | "location" | "date" | "time" | "true" | "false">,
    default: undefined,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  exact: {
    type: Boolean,
    default: false,
  },
  isActive: {
    type: Boolean,
    default: undefined,
  },
  inactiveClass: {
    type: String,
    default: null,
  },
  tooltip: {
    type: [String, Object],
    default: null,
  },
});
const attrs = useAttrs();
const link = useLink(props);

const isExternalLink = computed(() => {
  return typeof props.to === "string" && props.to.startsWith("http");
});

const isLinkActive = computed(() => {
  if (props.disabled) {
    return false;
  }
  if (typeof props.isActive === "boolean") {
    return props.isActive;
  }

  if (!link) {
    return false;
  }

  return props.exact ? link.isExactActive.value : link.isExactActive.value;
});
</script>

<template>
  <span
    v-if="props.disabled"
    v-tooltip="props.tooltip"
    v-bind="attrs"
    :class="props.isActive ? props.activeClass : props.inactiveClass"
    class="cursor-not-allowed"
  >
    <slot v-bind="{ active: props.isActive }" />
  </span>
  <a
    v-else-if="isExternalLink"
    v-tooltip="props.tooltip"
    v-bind="attrs"
    :href="props.to as string"
    target="_blank"
    :class="props.isActive ? props.activeClass : props.inactiveClass"
  >
    <slot v-bind="{ active: props.isActive }" />
  </a>
  <router-link v-else v-slot="{ href, navigate }" v-bind="props" custom>
    <a
      v-tooltip="props.tooltip"
      v-bind="attrs"
      :href="href"
      :class="isLinkActive ? props.activeClass : props.inactiveClass"
      @click="navigate"
    >
      <slot v-bind="{ active: isLinkActive }" />
    </a>
  </router-link>
</template>
